import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import * as moment from 'moment-timezone';
import { animate, style, transition, trigger } from "@angular/animations";
import { MenuService } from '../menu-service/menu.service';
import { Events } from "src/app/utils/event-utils";
import { AuthService } from 'src/app/components/auth/service/auth.service';
import { SocketService } from 'src/app/services/app/socket-service/socket-service.service';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OAuthService } from 'angular-oauth2-oidc';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  animations: [
    trigger("openCloseProfile", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate(
          "500ms ease-in-out",
          style({ opacity: 1, transform: "scale(1)" })
        )
      ]),
      transition(":leave", [
        style({ opacity: 1, }),
        animate(
          "300ms ease-in-out",
          style({ opacity: 0, transform: "scale(.95)" })
        )
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('audioOption', { static: true }) audioPlayerRef!: ElementRef;
  subMenus: any;
  showUserOptions = false;
  currentPath = '';
  isHome = false;
  timeZone: any;
  showQuickMenu = false;
  user: any;
  intervalTimeID: any;
  digitalClock: string = '';
  menus: any;
  isSound = true;
  userRole: any;
  isShowNotificationModal: boolean = false;
  isToastAlert: boolean = true;
  unreadNotificationCount = 0;
  isDarkMode = false;
  private recentNotificationTimestamps = new Map<string, number>();

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;

    if (this.isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }

  constructor(
    private sharedDataService: SharedDataService,
    private authService: AuthService,
    private menuService: MenuService,
    private socket: SocketService,
    private router: Router,
    private socketService: SocketService,
    private toastr: ToastrService,
    private oauthService: OAuthService
  ) { }

  ngOnInit() {
    this.setupClock();
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      this.isDarkMode = true;
      document.documentElement.classList.add('dark');
    }
    this.getUser();
    this.router.events
      .pipe(untilDestroyed(this)).subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (event.url) {
            let path = event.url;
            this.currentPath = path.substring(1);
            this.getSubMenus();
          }
        }
      });
    this.getSubMenus();
    this.listenSocket();
    this.sharedDataService.refreshUnreadNotificationCount();
      this.sharedDataService
        .getUnreadNotificationCount()
        .pipe(untilDestroyed(this))
        .subscribe(count => {
          this.unreadNotificationCount = count;
          this.updateDesktopBadgeAndTitle(count);
        });

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        this.sharedDataService.refreshUnreadNotificationCount();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.sharedDataService.refreshUnreadNotificationCount();
        }
      });
    }
    this.setupLocalStorage();
  }

  setupClock() {
    this.intervalTimeID = setInterval(() => {
      this.digitalClock = this.timeZone 
        ? `${moment().tz(this.timeZone).format('MM/DD')} - ${moment().tz(this.timeZone).format('HH:mm:ss')}`
        : '';
    }, 1000);
  }

  listenSocket() {
    this.socketService
      .addListener(Events.TRIP_NOTIFICATIONS)
      .pipe(untilDestroyed(this)).subscribe(res => {
        if (res) {
          this.onReceiveMessage(res, 'trip');
        }
      });
  }

  onReceiveMessage(res: any, type: string) {
    if (!this.isNotificationRelevant(res)) {
      return;
    }

    const notificationId = res?.id || res?.notificationId || res?._id;
    const dedupeKey = notificationId
      ? String(notificationId)
      : `${type}_${res?.title || ''}_${res?.message || ''}_${res?.data?.id || res?.data?._id || ''}`;

    const now = Date.now();
    const lastSeen = this.recentNotificationTimestamps.get(dedupeKey);
    if (lastSeen && now - lastSeen < 2500) {
      return;
    }
    this.recentNotificationTimestamps.set(dedupeKey, now);

    if (this.recentNotificationTimestamps.size > 100) {
      for (const [key, timestamp] of this.recentNotificationTimestamps.entries()) {
        if (now - timestamp > 10000) {
          this.recentNotificationTimestamps.delete(key);
        }
      }
    }

    const isUserActivelyOnScreen = typeof document !== 'undefined'
      && document.visibilityState === 'visible'
      && (typeof document.hasFocus === 'function' ? document.hasFocus() : true);

    if (isUserActivelyOnScreen) {
      // Screen is active: acknowledge notification to server as read
      if (notificationId) {
        this.sharedDataService
          .markNotificationAsRead(notificationId)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: () => this.sharedDataService.refreshUnreadNotificationCount(true),
            error: () => this.sharedDataService.refreshUnreadNotificationCount(true),
          });
      } else {
        this.sharedDataService.refreshUnreadNotificationCount();
      }
    } else {
      // Screen is in background/inactive: refresh unread count and show desktop notification
      this.sharedDataService.refreshUnreadNotificationCount();

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        const title = res?.title || 'Trip / Activity Alert';
        const body = res?.message || 'You have a new update in your account.';
        const options: any = {
          body,
          icon: '/assets/icons/notification-icon.png',
          badge: '/assets/icons/badge.png',
          data: { url: '/trips', raw: res }
        };

        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(reg => {
            reg.showNotification(title, options);
          }).catch(() => {
            new Notification(title, options);
          });
        } else {
          new Notification(title, options);
        }
      }
    }

    if (this.isToastAlert) {
      if (res.data && ['pending', 'scheduled'].includes(res.data.status)
        && (['accepted', 'rejected'].includes(res.data.pendingStatus))) {
        const settings: any = {
          timeOut: 0,
          extendedTimeOut: 0,
          closeButton: true,
          tapToDismiss: true
        };
        if (res.data.pendingStatus === 'accepted') {
          settings['toastClass'] = this.toastr.toastrConfig.toastClass + ' toast-accepted';
        } else if (res.data.pendingStatus === 'rejected') {
          settings['toastClass'] = this.toastr.toastrConfig.toastClass + ' toast-rejected';
        }
        this.toastr.success(res.message, 'Alert', settings);
      } else {
        this.toastr.success(res.message, 'Alert');
      }
    }

    if (this.isSound) {
      this.onAudioPlay();
    }
  }

  private isNotificationRelevant(data: any): boolean {
    if (!this.user || !data) {
      return true;
    }

    const tripMember = data?.data?.member || data?.member;
    let tripMemberId = null;

    if (typeof tripMember === 'object' && tripMember !== null) {
      tripMemberId = tripMember.id || tripMember._id;
    } else if (typeof tripMember === 'string') {
      tripMemberId = tripMember;
    }

    const userMemberId = this.user?.member?.id || this.user?.member?._id || this.user?.member;
    if (userMemberId && tripMemberId && String(userMemberId) !== String(tripMemberId)) {
      return false;
    }

    return true;
  }

  setupLocalStorage() {
    const sound = localStorage.getItem("sound");
    if (sound !== null) {
      this.isSound = JSON.parse(sound);
    }
    const notification = localStorage.getItem("isToastAlert");
    if (notification !== null) {
      this.isToastAlert = JSON.parse(notification);
    }
    window.addEventListener('storage', (event) => this.syncSoundAcrossTabs(event));
  }

  syncSoundAcrossTabs(event: StorageEvent) {
    if (event.key === "sound") {
      this.isSound = JSON.parse(event.newValue || 'true');
    }
    if (event.key === 'isToastAlert') {
      if (event.newValue) {
        this.isToastAlert = JSON.parse(event.newValue);
      }
    }
  }

  onAudioPlay() {
    if (this.isSound && this.audioPlayerRef?.nativeElement) {
      try {
        const playPromise = this.audioPlayerRef.nativeElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay policy prevented playback until user interaction - safe ignore
          });
        }
      } catch (err) {}
    }
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(untilDestroyed(this)).subscribe(user => {
        if (user) {
          this.user = user;
          this.timeZone = user.timeZone;
          if (this.user && this.user.roles) {
            if ((typeof this.user.roles) === 'string') {
              if (this.user.sRoles) {
                this.userRole = this.menuService.getUserRoleTitle(this.user.sRoles);
                this.menus = this.menuService.getMenu(this.user.sRoles, user);
              } else {
                this.userRole = this.menuService.getUserRoleTitle(this.user.roles);
                this.menus = this.menuService.getMenu(this.user.roles, user);
              }
            } else if ((typeof this.user.roles) === 'object') {
              this.userRole = this.menuService.getUserRoleTitle(this.user.roles[0]);
              this.menus = this.menuService.getMenu(this.user.roles[0], user);
            }
          }
        }
      });
  }

  getSubMenus() {
    this.sharedDataService
      .getSubMenus()
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        this.subMenus = [];
        if (data) {
          this.isHome = this.currentPath === data.key ? true : false;
          if (data.subMenus) {
            this.subMenus = data.subMenus;
            this.subMenus.forEach((menu: any) => {
              menu.isActive = this.highlightActiveMenu(menu);
            });
          }
        }
      });
  }

  onQuickMenuClick(menuKey: any) {
    this.subMenus = [];
    const menu = this.menus[menuKey];
    this.sharedDataService.saveSubMenus(menu ? menu : null);
  }

  logout() {
    this.socket.disconnectSocket();
    this.authService.removeToken('access_token');
    this.authService.removeToken('refresh_token');
    this.sharedDataService.saveUser(null);
    this.oauthService.logOut();
    this.router.navigateByUrl('login');
  }

  viewSmallScreenMenu() {
    this.sharedDataService.setSmallScreenMenuState(true);
  }

  onClickDropdownMenu() {
    this.sharedDataService.saveSubMenus(null);
    this.showUserOptions = !this.showUserOptions;
  }

  toggleSound() {
    this.isSound = !this.isSound;
    localStorage.setItem("sound", JSON.stringify(this.isSound));
  }

  canViewSubmenu(subMenus: any, title: any) {
    let isFound = false;
    if (subMenus && subMenus.length) {
      isFound = subMenus.some((m: any) => m.title === title);
    }
    return isFound;
  }

  openNotificationModal() {
    this.isShowNotificationModal = true;
  }

  closeActionMenu(evt: any) {
    this.showUserOptions = false;
  }

  toggleNotification() {
    this.isToastAlert = !this.isToastAlert;
    localStorage.setItem("isToastAlert", JSON.stringify(this.isToastAlert));
    if (!this.isToastAlert) {
      this.toastr.clear();
    }
  }

  highlightActiveMenu(menu: any) {
    let currentUrl = this.router.url.split(/[?#]/)[0];
    if (currentUrl.endsWith('/')) {
      currentUrl = currentUrl.slice(0, -1);
    }
    let menuUrl = menu.url.startsWith('/') ? menu.url : '/' + menu.url;
    if (menuUrl.endsWith('/')) {
      menuUrl = menuUrl.slice(0, -1);
    }
    if (currentUrl === menuUrl) {
      return true;
    }
    if (currentUrl.startsWith(menuUrl + '/')) {
      const specificRoute = this.subMenus.some((m: any) => {
        let mUrl = m.url.startsWith('/') ? m.url : '/' + m.url;
        if (mUrl.endsWith('/')) {
          mUrl = mUrl.slice(0, -1);
        }
        return (
          mUrl !== menuUrl &&
          currentUrl.startsWith(mUrl) &&
          mUrl.length > menuUrl.length
        );
      });
      return !specificRoute;
    }
    return false;
  }

  private updateDesktopBadgeAndTitle(count: number) {
    if (typeof window === 'undefined') return;

    const baseTitle = 'Member Panel';
    if (count > 0) {
      document.title = `(${count}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }

    if ('setAppBadge' in navigator && typeof (navigator as any).setAppBadge === 'function') {
      if (count > 0) {
        (navigator as any).setAppBadge(count).catch(() => {});
      } else {
        (navigator as any).clearAppBadge?.().catch(() => {});
      }
    }
  }

  ngOnDestroy(): void {
    if (this.intervalTimeID) {
      clearInterval(this.intervalTimeID);
    }
  }
}
